import { t } from '@/lib/i18n';
import type { RorosCanvasBlocksDTO, RorosCanvasItemDTO } from '@/types/roros/canvases';
import { CanvasBlock } from './CanvasBlock';
import { CanvasShell } from './CanvasShell';
import { AI_GAP_BLOCK_KEYS, VPC_CUSTOMER_PROFILE_BLOCKS, VPC_VALUE_MAP_BLOCKS } from './canvasConfig';

interface ValuePropositionCanvasProps {
  canEdit: boolean;
}

export function ValuePropositionCanvas({ canEdit }: ValuePropositionCanvasProps) {
  return (
    <CanvasShell
      type="vpc"
      title={t('Value Proposition Canvas')}
      description={t('Connect your Value Map to the Customer Profile with editable sticky-note sections.')}
      testId="value-proposition-canvas"
      canEdit={canEdit}
    >
      {({ blocks, canEdit: shellCanEdit, isSaving, isGenerating, aiAvailable, saveBlocks, regenerateBlock }) => {
        function updateBlock(blockKey: string, items: RorosCanvasItemDTO[]) {
          saveBlocks({
            ...blocks,
            [blockKey]: { items },
          } as RorosCanvasBlocksDTO);
        }

        return (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2" data-testid="vpc-two-panel-grid">
            <section className="rounded-2xl border border-primary-100 bg-primary-50/30 p-3" data-testid="vpc-value-map-panel">
              <h3 className="mb-3 text-lg font-semibold text-foreground">{t('Value Map')}</h3>
              <div className="grid grid-cols-1 gap-3">
                {VPC_VALUE_MAP_BLOCKS.map((block) => (
                  <CanvasBlock
                    key={block.key}
                    blockKey={block.key}
                    title={t(block.label)}
                    block={blocks[block.key]}
                    canEdit={shellCanEdit}
                    isSaving={isSaving}
                    isGenerating={isGenerating}
                    onItemsChange={(items) => updateBlock(block.key, items)}
                    onRegenerate={() => regenerateBlock(block.key)}
                    showAiConfigureAffordance={!aiAvailable && AI_GAP_BLOCK_KEYS.includes(block.key) && (blocks[block.key]?.items ?? []).length === 0}
                  />
                ))}
              </div>
            </section>
            <section className="rounded-2xl border border-secondary-100 bg-secondary-50/30 p-3" data-testid="vpc-customer-profile-panel">
              <h3 className="mb-3 text-lg font-semibold text-foreground">{t('Customer Profile')}</h3>
              <div className="grid grid-cols-1 gap-3">
                {VPC_CUSTOMER_PROFILE_BLOCKS.map((block) => (
                  <CanvasBlock
                    key={block.key}
                    blockKey={block.key}
                    title={t(block.label)}
                    block={blocks[block.key]}
                    canEdit={shellCanEdit}
                    isSaving={isSaving}
                    isGenerating={isGenerating}
                    onItemsChange={(items) => updateBlock(block.key, items)}
                    onRegenerate={() => regenerateBlock(block.key)}
                    showAiConfigureAffordance={!aiAvailable && AI_GAP_BLOCK_KEYS.includes(block.key) && (blocks[block.key]?.items ?? []).length === 0}
                  />
                ))}
              </div>
            </section>
          </div>
        );
      }}
    </CanvasShell>
  );
}
